// Where the backend is, and the session token — the two lowest-level facts every caller needs.
//
// Extracted from api.ts so that modules api.ts itself imports (notably agent-availability.ts,
// which api.ts consults for its /agent/* backstop) can resolve a URL without an import cycle.
// api.ts re-exports everything here, so no existing call site changed.
//
// Keeping the base-URL rule in ONE place is deliberate: a second, subtly different copy of it is
// exactly what produced the export corruption (a relative /v1 URL in prod reaches the Static Web
// App, whose navigationFallback answers 200 with index.html, and the caller saves the app shell
// as a .docx). One implementation, imported everywhere.

const TOKEN_KEY = 'niobe.token';

/**
 * REST base for /v1 requests. Empty string → same-origin (dev, via the Vite proxy). In prod a
 * runtime config script (public/config.js) sets window.__NIOBE_CONFIG__.backendUrl to the backend
 * origin. Resolved per call so a late-loading config or a test stub is honoured.
 */
export function backendUrl(): string {
  // A hand-written or generated config.js may carry a trailing slash; strip it so apiUrl() can
  // never emit a doubled '//v1'.
  return (window.__NIOBE_CONFIG__?.backendUrl ?? import.meta.env.VITE_BACKEND_URL ?? '').replace(/\/+$/, '');
}

/**
 * Absolute URL for a backend path. `path` is relative to the backend ORIGIN and starts with '/'
 * (e.g. '/v1/export?…', or a whole '/v1/export-files/<bid>/<file>' handed back by the API) — NOT
 * relative to /v1, because callers either build their own query string or receive complete paths.
 *
 * An empty base returns `path` unchanged, i.e. a same-origin relative URL, so the dev flow through
 * the Vite proxy is byte-identical to before.
 */
export function apiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path; // already absolute — leave it alone
  return `${backendUrl()}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * "local" vs "prod", for the footer's profile chip. An empty base (dev, same-origin via the Vite
 * proxy) is local, and so is a localhost/127.0.0.1 base — that's the DESKTOP build, which reaches
 * its own local passerelle (see client/desktop/scripts/web-bundle.mjs, same regex). Anything else
 * is a real backend origin: the Azure Static Web Apps build (client/web/scripts/prod-build.mjs).
 */
export function deployProfile(): 'local' | 'prod' {
  const url = backendUrl();
  if (!url) return 'local';
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/|$)/.test(url) ? 'local' : 'prod';
}

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}
